import Box from "@mui/material/Box";
import { useTheme } from "@mui/material";

import isString from "lodash/isString";
import { useEffect, useMemo, useRef, useState } from "react";
import Placeholder from "@tiptap/extension-placeholder";
// ─── TipTap Imports ───────────────────────────────────────────────────────────
// npm install @tiptap/react @tiptap/starter-kit @tiptap/extension-color
//             @tiptap/extension-text-style @tiptap/extension-underline
//             @tiptap/extension-link @tiptap/extension-mention
//             @tiptap/extension-font-size @tiptap/extension-text-align
//             @tiptap/pm
// If @tiptap/extension-font-size is unavailable, see the inline extension below.

import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-text-style";
import Mention from "@tiptap/extension-mention";
import { useSelector } from "react-redux";

// ─── Inline FontSize extension (avoids extra package) ─────────────────────────
// If you have @tiptap/extension-font-size installed, swap this out for:
//   import FontSize from "@tiptap/extension-font-size";

const FontSize = TextStyle.extend({
  addGlobalAttributes() {
    return [
      {
        types: ["textStyle"],
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element) => element.style.fontSize || null,
            renderHTML: (attributes) => {
              if (!attributes.fontSize) return {};
              return { style: `font-size: ${attributes.fontSize}` };
            },
          },
        },
      },
    ];
  },
});

// ─── Toolbar Button ───────────────────────────────────────────────────────────

const ToolbarButton = ({ onClick, isActive, disabled, children, title }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 28,
      height: 28,
      border: "none",
      borderRadius: 4,
      cursor: disabled ? "default" : "pointer",
      background: isActive ? "#e0e7ff" : "transparent",
      color: isActive ? "#3b5998" : disabled ? "#ccc" : "#555",
      fontSize: 13,
      fontWeight: isActive ? 700 : 500,
      transition: "background 0.15s, color 0.15s",
    }}
    onMouseEnter={(e) => {
      if (!disabled)
        e.currentTarget.style.background = isActive ? "#e0e7ff" : "#f1f5f9";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = isActive ? "#e0e7ff" : "transparent";
    }}
  >
    {children}
  </button>
);

// ─── Font Size Dropdown ───────────────────────────────────────────────────────

const FONT_SIZES = [8, 9, 10, 11, 12, 14, 16, 18, 24, 30, 36, 48, 60, 72, 96];

const FontSizeDropdown = ({ editor }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Detect current font size
  const currentSize =
    editor?.getAttributes("textStyle")?.fontSize?.replace("px", "") || "12";

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const applySize = (size) => {
    editor
      .chain()
      .focus()
      .setMark("textStyle", { fontSize: `${size}px` })
      .run();
    setOpen(false);
  };

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block" }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 2,
          height: 28,
          padding: "0 8px",
          border: "1px solid #e2e8f0",
          borderRadius: 4,
          background: "#fff",
          cursor: "pointer",
          fontSize: 12,
          fontWeight: 600,
          color: "#475569",
          minWidth: 44,
        }}
      >
        {currentSize}
        <span style={{ fontSize: 10, marginLeft: 2 }}>▾</span>
      </button>
      {open && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            zIndex: 1000,
            background: "#fff",
            border: "1px solid #e2e8f0",
            borderRadius: 6,
            boxShadow: "0 4px 12px rgba(0,0,0,0.10)",
            maxHeight: 200,
            overflowY: "auto",
            marginTop: 2,
            minWidth: 50,
          }}
        >
          {FONT_SIZES.map((size) => (
            <div
              key={size}
              onClick={() => applySize(size)}
              style={{
                padding: "4px 12px",
                cursor: "pointer",
                fontSize: 12,
                fontWeight: String(size) === currentSize ? 700 : 400,
                background:
                  String(size) === currentSize ? "#f1f5f9" : "transparent",
                color: "#334155",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = "#f1f5f9")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background =
                  String(size) === currentSize ? "#f1f5f9" : "transparent")
              }
            >
              {size}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Color Picker Button ──────────────────────────────────────────────────────

const ColorPickerButton = ({ editor }) => {
  const inputRef = useRef(null);
  const currentColor = editor?.getAttributes("textStyle")?.color || "#000000";

  return (
    <div
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
      }}
    >
      <ToolbarButton
        title="Text Color"
        onClick={() => inputRef.current?.click()}
      >
        <span
          style={{ borderBottom: `2px solid ${currentColor}`, lineHeight: 1 }}
        >
          A
        </span>
      </ToolbarButton>
      <input
        ref={inputRef}
        type="color"
        value={currentColor}
        onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
        style={{
          position: "absolute",
          width: 0,
          height: 0,
          opacity: 0,
          pointerEvents: "none",
        }}
      />
    </div>
  );
};

// ─── Toolbar ──────────────────────────────────────────────────────────────────

const EditorToolbar = ({ editor }) => {
  if (!editor) return null;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        padding: "6px 8px",
        borderBottom: "1px solid rgba(0,0,0,0.08)",
        background: "#fafbfc",
        flexWrap: "wrap",
      }}
    >
      <FontSizeDropdown editor={editor} />

      <div
        style={{ width: 1, height: 18, background: "#e2e8f0", margin: "0 4px" }}
      />

      <ColorPickerButton editor={editor} />

      <div
        style={{ width: 1, height: 18, background: "#e2e8f0", margin: "0 4px" }}
      />

      <ToolbarButton
        title="Bold"
        onClick={() => editor.chain().focus().toggleBold().run()}
        isActive={editor.isActive("bold")}
      >
        <strong>B</strong>
      </ToolbarButton>
      <ToolbarButton
        title="Italic"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        isActive={editor.isActive("italic")}
      >
        <em>I</em>
      </ToolbarButton>
      <ToolbarButton
        title="Underline"
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        isActive={editor.isActive("underline")}
      >
        <span style={{ textDecoration: "underline" }}>U</span>
      </ToolbarButton>
      <ToolbarButton
        title="Strikethrough"
        onClick={() => editor.chain().focus().toggleStrike().run()}
        isActive={editor.isActive("strike")}
      >
        <span style={{ textDecoration: "line-through" }}>S</span>
      </ToolbarButton>

      <div
        style={{ width: 1, height: 18, background: "#e2e8f0", margin: "0 4px" }}
      />

      <ToolbarButton
        title="Bullet List"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        isActive={editor.isActive("bulletList")}
      >
        •≡
      </ToolbarButton>
      <ToolbarButton
        title="Ordered List"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        isActive={editor.isActive("orderedList")}
      >
        1.
      </ToolbarButton>

      <div
        style={{ width: 1, height: 18, background: "#e2e8f0", margin: "0 4px" }}
      />

      <ToolbarButton
        title="Link"
        onClick={() => {
          const url = window.prompt("Enter URL:");
          if (url) {
            editor.chain().focus().setLink({ href: url }).run();
          }
        }}
        isActive={editor.isActive("link")}
      >
        🔗
      </ToolbarButton>
      <ToolbarButton
        title="Unlink"
        onClick={() => editor.chain().focus().unsetLink().run()}
        disabled={!editor.isActive("link")}
      >
        ⊘
      </ToolbarButton>

      <div
        style={{ width: 1, height: 18, background: "#e2e8f0", margin: "0 4px" }}
      />

      <ToolbarButton
        title="Undo"
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
      >
        ↩
      </ToolbarButton>
      <ToolbarButton
        title="Redo"
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
      >
        ↪
      </ToolbarButton>
      <ToolbarButton
        title="Clear Formatting"
        onClick={() =>
          editor.chain().focus().clearNodes().unsetAllMarks().run()
        }
      >
        ✕
      </ToolbarButton>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const HtmlEditor = ({
  sx,
  onChange,
  editorRootSx,
  editorToolbarSx,
  editorMainSx,
  suppressOuterBorder,
  maxHeight = "240px",
  readOnly = false,
  id,
  minHeight,
  placeholder = "Start typing...",
  borderRadius = "8px",
  backgroundColor,
  children,
  /**
   * Accepts an HTML string to pre-populate the editor.
   */
  value,
}) => {
  const theme = useTheme();
  const users = useSelector((state) => state?.users);
  const lastExternalValue = useRef(null);

  // Build mention suggestion items from users
  const mentionSuggestionItems = useMemo(
    () =>
      (users || [])
        .map((user) => ({
          id: user?.emailAddress,
          label: `${user?.fullName} <${user?.emailAddress}>`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [users],
  );

  // Mention suggestion renderer
  const mentionSuggestion = useMemo(
    () => ({
      items: ({ query }) => {
        const filtered = mentionSuggestionItems.filter((item) =>
          item.label.toLowerCase().includes(query.toLowerCase()),
        );
        return query ? filtered.slice(0, 8) : filtered;
      },
      render: () => {
        let component;
        let popup;

        return {
          onStart: (props) => {
            const el = document.createElement("div");
            el.style.cssText = `
              position: fixed;
              z-index: 9999;
              background: #fff;
              border: 1px solid #e2e8f0;
              border-radius: 8px;
              box-shadow: 0 4px 16px rgba(0,0,0,0.12);
              max-height: 200px;
              overflow-y: auto;
              padding: 4px 0;
            `;
            document.body.appendChild(el);
            popup = el;
            component = props;
            updatePopup(props);
          },
          // onUpdate: ({ editor: ed }) => {
          //   if (!onChange) return;
          //   let htmlString = ed?.getHTML();
          //   // Strip trailing empty paragraph tags
          //   htmlString = htmlString.replace(/(<p><\/p>)+$/i, "");
          //   const plainText = ed?.getText();
          //   onChange({
          //     htmlString,
          //     plainText,
          //     editorInstance: ed,
          //   });
          // },
          onUpdate: (props) => {
            component = props;
            updatePopup(props);
          },
          onKeyDown: (props) => {
            if (props.event.key === "Escape") {
              popup?.remove();
              return true;
            }
            return false;
          },
          onExit: () => {
            popup?.remove();
          },
        };

        function updatePopup(props) {
          if (!popup) return;
          const { clientRect, items, command } = props;
          if (!clientRect) return;

          const rect = clientRect();
          popup.style.top = `${rect.bottom + 4}px`;
          popup.style.left = `${rect.left}px`;

          popup.innerHTML = "";
          items.forEach((item, index) => {
            const div = document.createElement("div");
            div.textContent = item.label;
            div.style.cssText = `
              padding: 6px 12px;
              cursor: pointer;
              font-size: 12px;
              color: #334155;
            `;
            div.addEventListener("mouseenter", () => {
              div.style.background = "#f1f5f9";
            });
            div.addEventListener("mouseleave", () => {
              div.style.background = "transparent";
            });
            div.addEventListener("mousedown", (e) => {
              e.preventDefault();
              command(item);
            });
            popup.appendChild(div);
          });

          if (items.length === 0) {
            popup.innerHTML =
              '<div style="padding:8px 12px;font-size:12px;color:#94a3b8;">No results</div>';
          }
        }
      },
    }),
    [mentionSuggestionItems],
  );

  // TipTap editor instance
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3, 4, 5, 6],
        },
      }),
      Underline,
      TextStyle,
      FontSize,
      Color,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          rel: "noopener noreferrer",
          target: "_blank",
        },
      }),
      Placeholder.configure({
        placeholder,
        showOnlyWhenEditable: true,
        showOnlyCurrent: true,
      }),
      Mention.configure({
        HTMLAttributes: {
          class: "wysiwyg-mention",
        },
        suggestion: mentionSuggestion,
        renderHTML: ({ node }) => {
          return [
            "a",
            {
              class: "wysiwyg-mention",
              "data-mention": "",
              "data-value": node.attrs.id,
              href: "#",
            },
            `@${node.attrs.label || node.attrs.id}`,
          ];
        },
      }),
    ],
    content: "",
    editable: !readOnly,
    editorProps: {
      attributes: {
        // class: "tiptap-editor-content",
        class: "tiptap-prosemirror",
        style: `font-family: var(--primaryFont); font-size: 12px;`,
      },
    },
    onUpdate: ({ editor: ed }) => {
      if (!onChange) return;
      const htmlString = ed.getHTML();
      const plainText = ed.getText();
      onChange({
        htmlString,
        plainText,
        editorInstance: ed,
      });
    },
  });

  // Sync external `value` prop into editor
  // Sync external `value` prop into editor
  // useEffect(() => {
  //   if (!editor) return;

  //   // Strip trailing empty paragraph tags
  //   const cleanValue = isString(value)
  //     ? value.replace(/(<p><\/p>)+$/i, "")
  //     : value;

  //   if (isString(cleanValue) && cleanValue.trim() !== "") {
  //     if (lastExternalValue.current !== cleanValue) {
  //       lastExternalValue.current = cleanValue;
  //       editor.commands.setContent(cleanValue, false);
  //     }
  //   } else if (
  //     !cleanValue ||
  //     (isString(cleanValue) && cleanValue.trim() === "")
  //   ) {
  //     if (lastExternalValue.current !== "") {
  //       lastExternalValue.current = "";
  //       editor.commands.clearContent();
  //     }
  //   }
  // }, [value, editor]);
  useEffect(() => {
    if (!editor) return;

    if (isString(value) && value.trim() !== "") {
      // Only set content if the value actually changed from outside
      // (prevents cursor-resetting loops during typing)
      if (lastExternalValue.current !== value) {
        lastExternalValue.current = value;
        editor.commands.setContent(value, false);
      }
    } else if (!value || (isString(value) && value.trim() === "")) {
      if (lastExternalValue.current !== "") {
        lastExternalValue.current = "";
        editor.commands.clearContent();
      }
    }
  }, [value, editor]);

  // Update editable state if readOnly prop changes
  useEffect(() => {
    if (editor) {
      editor.setEditable(!readOnly);
    }
  }, [readOnly, editor]);

  // Check if content is empty (for conditional toolbar display)
  const isEditorEmpty = !editor?.getText()?.trim();

  // ─── Styles ───────────────────────────────────────────────────────────────

  const rootStyles = useMemo(() => {
    if (readOnly) {
      return {
        "& .tiptap-editor-content": {
          "div, span, p, li": {
            fontFamily: "var(--primaryFont)",
            fontSize: "12px",
          },
          "& p": {
            margin: "0 0 0.4em 0",
            fontFamily: "var(--primaryFont)",
            fontSize: "12px",
            lineHeight: 1.5,
          },
          "& ul, & ol": {
            paddingLeft: "1.5em",
            margin: "0.25em 0",
            fontFamily: "var(--primaryFont)",
            fontSize: "12px",
          },
          "& ol": {
            listStyleType: "decimal",
          },
          "& ul": {
            listStyleType: "disc",
          },
          "& li": {
            fontFamily: "var(--primaryFont)",
            fontSize: "12px",
            lineHeight: 1.5,
          },
          "& a": {
            color: theme.palette.primary?.main || "#1976d2",
            textDecoration: "none",
            "&:hover": { textDecoration: "underline" },
          },
          "& .wysiwyg-mention": {
            color: theme.palette.primary?.main || "#1976d2",
            fontWeight: 600,
            cursor: "pointer",
            "&:hover": { textDecoration: "underline" },
          },
          ...editorMainSx,
        },
        ...sx,
      };
    }

    return {
      display: "flex",
      flexDirection: "column",
      width: "100%",
      borderRadius,
      backgroundColor: "#fff",
      border: suppressOuterBorder
        ? "none"
        : `1px solid ${theme.palette.separator || "rgba(0,0,0,0.12)"}`,
      overflow: "hidden",
      ...editorRootSx,

      // Editor content area
      "& .tiptap-editor-content": {
        backgroundColor: backgroundColor || "#fff",
        minHeight: isEditorEmpty ? minHeight : "180px",
        maxHeight,
        overflowY: "auto",
        borderBottomLeftRadius: borderRadius,
        borderBottomRightRadius: borderRadius,
        ...editorMainSx,
      },

      "& .tiptap-prosemirror": {
        outline: "none",
        px: 1.5,
        py: 1,

        "& p": {
          margin: "0 0 0.4em 0",
          fontFamily: "var(--primaryFont)",
          fontSize: "12px",
          lineHeight: 1.5,
        },
        "& ul, & ol": {
          paddingLeft: "1.5em",
          margin: "0.25em 0",
          fontFamily: "var(--primaryFont)",
          fontSize: "12px",
        },
        "& li": {
          fontFamily: "var(--primaryFont)",
          fontSize: "12px",
          lineHeight: 1.5,
        },
        "& h1, & h2, & h3, & h4, & h5, & h6": {
          fontFamily: "var(--primaryFont)",
          marginTop: "0.6em",
          marginBottom: "0.3em",
        },
        "& h6": {
          fontSize: "12px",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          fontWeight: 700,
        },
        "& a": {
          color: theme.palette.primary?.main || "#1976d2",
          textDecoration: "none",
          "&:hover": { textDecoration: "underline" },
        },
        "& .wysiwyg-mention": {
          color: theme.palette.primary?.main || "#1976d2",
          fontWeight: 600,
          cursor: "pointer",
          "&:hover": { textDecoration: "underline" },
        },
        "& p.is-editor-empty:first-child::before": {
          content: "attr(data-placeholder)",
          float: "left",
          color: "#adb5bd",
          pointerEvents: "none",
          height: 0,
          fontStyle: "italic",
          fontSize: "12px",
        },
      },
      // "& .tiptap-editor-content": {
      //   backgroundColor: backgroundColor || "#fff",
      //   minHeight: isEditorEmpty ? minHeight : "180px",
      //   maxHeight,
      //   overflowY: "auto",
      //   px: 1.5,
      //   py: 1,
      //   outline: "none",
      //   borderBottomLeftRadius: borderRadius,
      //   borderBottomRightRadius: borderRadius,

      //   "& p": {
      //     margin: "0 0 0.4em 0",
      //     fontFamily: "var(--primaryFont)",
      //     fontSize: "12px",
      //     lineHeight: 1.5,
      //   },
      //   "& ul, & ol": {
      //     paddingLeft: "1.5em",
      //     margin: "0.25em 0",
      //     fontFamily: "var(--primaryFont)",
      //     fontSize: "12px",
      //   },
      //   "& li": {
      //     fontFamily: "var(--primaryFont)",
      //     fontSize: "12px",
      //     lineHeight: 1.5,
      //   },
      //   "& h1, & h2, & h3, & h4, & h5, & h6": {
      //     fontFamily: "var(--primaryFont)",
      //     marginTop: "0.6em",
      //     marginBottom: "0.3em",
      //   },
      //   "& h6": {
      //     fontSize: "12px",
      //     textTransform: "uppercase",
      //     letterSpacing: "0.04em",
      //     fontWeight: 700,
      //   },
      //   "& a": {
      //     color: theme.palette.primary?.main || "#1976d2",
      //     textDecoration: "none",
      //     "&:hover": { textDecoration: "underline" },
      //   },
      //   "& .wysiwyg-mention": {
      //     color: theme.palette.primary?.main || "#1976d2",
      //     fontWeight: 600,
      //     cursor: "pointer",
      //     "&:hover": { textDecoration: "underline" },
      //   },
      //   "& .tiptap p.is-editor-empty:first-child::before": {
      //     content: "attr(data-placeholder)",
      //     float: "left",
      //     color: "#adb5bd",
      //     pointerEvents: "none",
      //     height: 0,
      //     fontStyle: "italic",
      //     fontSize: "12px",
      //   },

      //   ...editorMainSx,
      // },

      ...sx,
    };
  }, [
    borderRadius,
    suppressOuterBorder,
    editorRootSx,
    editorMainSx,
    backgroundColor,
    minHeight,
    maxHeight,
    sx,
    theme,
    readOnly,
    isEditorEmpty,
  ]);

  return (
    <Box className="html-text-editor-root" id={id} sx={rootStyles}>
      {!readOnly && !isEditorEmpty && <EditorToolbar editor={editor} />}
      <EditorContent editor={editor} className="tiptap-editor-content" />
      {children}
    </Box>
  );
};

// ─── Utility: Extract all @mentions from editor HTML ──────────────────────────

export const getAllTipTapMentions = (htmlString) => {
  if (!htmlString) return [];
  const mentions = [];
  const regex = /data-value="([^"]+)"/g;
  let match;
  while ((match = regex.exec(htmlString)) !== null) {
    if (match[1] && match[1].includes("@")) {
      mentions.push(match[1]);
    }
  }
  return [...new Set(mentions)];
};

// Backward-compat alias — GenericEmailEditor calls getAllDraftJsMentions
export const getAllDraftJsMentions = (editorStateOrHtml) => {
  // If someone passes an HTML string, parse mentions from it
  if (isString(editorStateOrHtml)) {
    return getAllTipTapMentions(editorStateOrHtml);
  }
  // If it's the TipTap editor instance
  if (editorStateOrHtml?.getHTML) {
    return getAllTipTapMentions(editorStateOrHtml.getHTML());
  }
  return [];
};

export default HtmlEditor;
